import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router';
import { Cookie, X } from 'lucide-react';
import { api } from '../services/api';

const CONSENT_KEY = 'cookie-consent';
const CONSENT_DATE_KEY = 'cookie-consent-date';

export type ConsentValue = 'accepted' | 'essential-only';

/** Read the current consent value from localStorage */
export function getConsentValue(): ConsentValue | null {
  return localStorage.getItem(CONSENT_KEY) as ConsentValue | null;
}

/** Check if the user has accepted analytics/advertising cookies */
export function hasAnalyticsConsent(): boolean {
  return getConsentValue() === 'accepted';
}

function isAuthenticated(): boolean {
  const stored = localStorage.getItem('weather-story-auth');
  if (!stored) return false;
  try {
    const { state } = JSON.parse(stored);
    return !!state?.accessToken;
  } catch {
    return false;
  }
}

/** Save consent choice to localStorage and sync to server if authenticated */
export function setConsent(value: ConsentValue) {
  localStorage.setItem(CONSENT_KEY, value);
  localStorage.setItem(CONSENT_DATE_KEY, new Date().toISOString());
  window.dispatchEvent(new CustomEvent('cookie-consent-change', { detail: value }));

  if (isAuthenticated()) {
    syncConsentToServer(value);
  }
}

/** Clear consent (re-shows the banner) */
export function clearConsent() {
  localStorage.removeItem(CONSENT_KEY);
  localStorage.removeItem(CONSENT_DATE_KEY);
  window.dispatchEvent(new CustomEvent('cookie-consent-change', { detail: null }));
}

/** Sync local consent to server (fire-and-forget) */
export function syncConsentToServer(value: ConsentValue) {
  api.put('/users/me/cookie-consent', { consent: value, source: 'web' }).catch(() => {});
}

/** Fetch server consent and apply locally (used after login) */
export async function syncConsentFromServer() {
  try {
    const response = await api.get<{ cookieConsent: string | null; cookieConsentDate: string | null }>(
      '/users/me/cookie-consent'
    );
    const serverConsent = response.data?.cookieConsent as ConsentValue | null;
    const localConsent = getConsentValue();

    if (serverConsent && !localConsent) {
      // Server has consent but local doesn't — apply server value
      localStorage.setItem(CONSENT_KEY, serverConsent);
      localStorage.setItem(CONSENT_DATE_KEY, response.data?.cookieConsentDate || new Date().toISOString());
      window.dispatchEvent(new CustomEvent('cookie-consent-change', { detail: serverConsent }));
    } else if (localConsent && !serverConsent) {
      // Local has consent but server doesn't — push local to server
      syncConsentToServer(localConsent);
    }
  } catch {
    // Silent fail
  }
}

export function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const stored = getConsentValue();
    if (!stored) {
      const timer = setTimeout(() => setVisible(true), 500);
      return () => clearTimeout(timer);
    }
  }, []);

  // Listen for external resets (e.g. from Settings page)
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (!detail) setVisible(true);
    };
    window.addEventListener('cookie-consent-change', handler);
    return () => window.removeEventListener('cookie-consent-change', handler);
  }, []);

  const handleAcceptAll = useCallback(() => {
    setConsent('accepted');
    setVisible(false);
  }, []);

  const handleEssentialOnly = useCallback(() => {
    setConsent('essential-only');
    setVisible(false);
  }, []);

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 inset-x-0 z-50 p-4 sm:p-6 pointer-events-none">
      <div className="max-w-lg mx-auto pointer-events-auto">
        <div className="bg-gray-800 border border-gray-600 rounded-2xl shadow-2xl shadow-black/40 p-5">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-primary-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Cookie className="h-5 w-5 text-primary-400" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-gray-100 text-sm">Cookie Preferences</h3>
                <button
                  onClick={handleEssentialOnly}
                  className="text-gray-500 hover:text-gray-300 -mr-1"
                  aria-label="Dismiss"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <p className="text-sm text-gray-400 mb-4">
                We use essential cookies for authentication and optional cookies for ads and analytics.{' '}
                <Link to="/privacy" className="text-primary-400 hover:text-primary-300 underline underline-offset-2">
                  Privacy Policy
                </Link>
              </p>
              <div className="flex gap-3">
                <button
                  onClick={handleAcceptAll}
                  className="btn-primary text-sm px-4 py-2"
                >
                  Accept All
                </button>
                <button
                  onClick={handleEssentialOnly}
                  className="btn-ghost text-sm px-4 py-2"
                >
                  Essential Only
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
